import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject, timer } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, tap, takeUntil } from 'rxjs/operators';
import { IBasicInfo } from 'src/app/appInterfaces/IBasicInfo';
import { IRoute } from 'src/app/appInterfaces/IRoute';
import { IOrderModel, IOrderStatusModel } from 'src/app/appInterfaces/IOrderModel';
import { IOrderType } from 'src/app/appInterfaces/IOrderType';
import { KdsService } from 'src/app/appServices/kds/kds.service';
import { OrderService } from 'src/app/appServices/order.service';
import { DateTimeUtility } from 'src/app/appUtilities/dateTime.utility';
import { KdsOrderSortDirection, OrderStatus, OrderType } from 'src/app/appEnums/enums';
import { AudioService } from '../audio.service';
import { IStore } from 'src/app/appInterfaces/IStore';
import { StoreResolver } from '../store/store.resolver';
import { KdsLineDoneStateService } from './kds-line-done-state.service';
import { StoresService } from '../store/stores.service';
import { SettingsResolver } from '../settings/settings.resolver';

interface PendingStatusTransition {
  targetStatus: string;
  expiresAt: number;
  visibleStatusModel?: IOrderStatusModel;
}

@Injectable({
  providedIn: 'root'
})
export class KdsResolver {
  orders = new BehaviorSubject<any[]>(null);
  loading = new BehaviorSubject<boolean>(false);
  routes = new BehaviorSubject<IRoute[]>(null);
  selectedRoute = new BehaviorSubject<IRoute>(null);
  selectedStatus = new BehaviorSubject<IOrderStatusModel>(null);
  connectionState = new BehaviorSubject<'online' | 'syncing' | 'stale' | 'error'>('online');
  lastSyncAt = new BehaviorSubject<number | null>(null);
  availableStausList: IOrderStatusModel[] = new Array();
  enableOrderSound = new BehaviorSubject<boolean>(true);
  selectedStore: IStore;

  destroy$ = new Subject<void>();
  orderStream$ = new Subject<void>();
  private cancelActiveOrderLoad$ = new Subject<void>();
  private pendingStatusTransitions = new Map<string, PendingStatusTransition>();
  private pendingRemovedOrders = new Map<string, number>();
  private readonly statusTransitionHoldMs = 30000;
  private readonly orderPollIntervalMs = 5000;
  private readonly staleSyncMs = 20000;
  private readonly orderDateLeewayMs = 60 * 60 * 1000;
  private readonly basicInfoStorageKey = 'basic-info';
  private readonly selectedRouteStoragePrefix = 'kdsSelectedRoute';
  private orderRefreshInFlight = false;
  private queuedOrderRefresh = false;
  private initialized = false;
  private routesStoreId: number = null;

  constructor(
    private kdsService: KdsService,
    private _orderService: OrderService,
    private _audioService: AudioService,
    private _storeResolver: StoreResolver,
    private dateUtility: DateTimeUtility,
    private _kdsLineDoneState: KdsLineDoneStateService,
    private _settingsResolver: SettingsResolver,
    private _storesService: StoresService) {
  }

  /**
   * Init Apis
   */
  initApis() {
    this._settingsResolver.bindRestaurantSettings();
    if (!this.initialized) {
      this.subscribeOrderStream();
      this.subscribeSelectedStoreRoutes();
      this.subscribeOrderFilters();
      this.subscribeOrderSortDirection();
      this.initialized = true;
    }
    this.loadAvailableStatusList();
    this.loadRoutesForSelectedStore();
    this.startOrderTimer();
  }


  /**
   * Call order stream
   */
  callOrderStream() {
    this.orderStream$.next();
  }

  /**
   * Subscribe order stream
   */
  subscribeOrderStream() {
    this.orderStream$.pipe(
      debounceTime(500),
      takeUntil(this.destroy$),
    ).subscribe(() => this.refreshOrders());
  }

  /**
   * Subscribe selected KDS filters
   */
  subscribeOrderFilters() {
    combineLatest([
      this._storeResolver.selectedStore,
      this.selectedRoute,
      this.selectedStatus,
    ]).pipe(
      takeUntil(this.destroy$),
      tap(([store]) => {
        this.selectedStore = store;
      }),
      debounceTime(0),
      distinctUntilChanged(([prevStore, prevRoute, prevStatus], [store, route, status]) => {
        return prevStore?.id === store?.id
          && prevRoute?.name === route?.name
          && prevStatus?.id === status?.id
          && prevStatus?.status === status?.status;
      }),
      tap(() => {
        this.queuedOrderRefresh = false;
        this.cancelActiveOrderLoad$.next();
        this.orders.next(null);
      }),
    ).subscribe(() => this.refreshOrders());
  }

  private subscribeSelectedStoreRoutes() {
    this._storeResolver.selectedStore.pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged((previousStore, store) => previousStore?.id === store?.id),
    ).subscribe((store) => {
      this.selectedStore = store;
      this.queuedOrderRefresh = false;
      this.cancelActiveOrderLoad$.next();
      this.orders.next(null);

      if (!store?.id) {
        this.routesStoreId = null;
        this.routes.next(null);
        this.selectedRoute.next(null);
        this.selectedStatus.next(null);
        return;
      }

      if (this.routesStoreId !== store.id) {
        this.routes.next(null);
        this.selectedRoute.next(null);
        this.selectedStatus.next(null);
      }
      this.loadRoutesForStore(store.id);
    });
  }

  private canLoadOrders(): boolean {
    const store = this.selectedStore || this._storeResolver.selectedStore.value;
    return !!store?.id && !!this.selectedRoute.value?.name && !!this.selectedStatus.value?.status;
  }


  /**
   * Toggle Order Sound
   */
  toggleOrderSound() {
    this.enableOrderSound.next(
      (this.enableOrderSound?.value) ? false : true
    )
  }


  /**
   * get all routes
   */
  loadRoutes() {
    this.loadRoutesForSelectedStore();
  }

  private loadRoutesForSelectedStore() {
    const store = this.selectedStore || this._storeResolver.selectedStore.value;
    if (!store?.id) {
      return;
    }
    this.loadRoutesForStore(store.id);
  }

  private loadRoutesForStore(storeId: number) {
    if (this.routesStoreId === storeId && this.routes.value?.length) {
      return;
    }

    forkJoin({
      routes: this.kdsService.getAllRoutes().pipe(catchError(() => of([]))),
      statuses: this.kdsService.getOrderStatusList().pipe(catchError(() => of([]))),
      orderTypes: this._storesService.getAllOrderTypes(storeId).pipe(catchError(() => of([]))),
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe(({ routes, statuses, orderTypes }: { routes: any[]; statuses: IOrderStatusModel[]; orderTypes: IOrderType[] }) => {
      if (this._storeResolver.selectedStore.value?.id !== storeId) {
        return;
      }

      if (statuses?.length) {
        this.availableStausList = statuses;
      }

      const normalizedRoutes = this.normalizeRoutesForStore(routes, statuses || [], orderTypes || []);
      if (!normalizedRoutes?.length) {
        this.routesStoreId = storeId;
        this.routes.next([]);
        this.selectedRoute.next(null);
        this.selectedStatus.next(null);
        this.orders.next(null);
        return;
      }

      this.routesStoreId = storeId;
      this.routes.next(normalizedRoutes);
      this.setSelectedRoute(this.getInitialRouteForStore(normalizedRoutes, storeId));
    });
  }



  /**
   * Set Selected route
   * @param route 
   */
  setSelectedRoute(route: IRoute) {
    if (!route) {
      this.selectedRoute.next(null);
      this.selectedStatus.next(null);
      return;
    }

    this.selectedRoute.next(route);
    this.persistSelectedRoute(route);
    this.setStatusForSelectedRoute(route);
  }

  private setStatusForSelectedRoute(route: IRoute) {
    const routeStatuses = route?.orderStatusList || [];
    const currentStatus = this.selectedStatus.value?.status;
    const matchingStatus = currentStatus
      ? routeStatuses.find((status) => status.status === currentStatus)
      : null;
    this.selectedStatus.next(matchingStatus || routeStatuses[0] || null);
  }

  private getInitialRouteForStore(routes: IRoute[], storeId: number): IRoute {
    const storedRoute = this.getStoredSelectedRoute(storeId);
    if (storedRoute) {
      const matchingStoredRoute = this.findMatchingRoute(routes, storedRoute);
      if (matchingStoredRoute) {
        return matchingStoredRoute;
      }
    }

    return routes[0];
  }

  private findMatchingRoute(routes: IRoute[], route: Partial<IRoute>): IRoute {
    if (!routes?.length || !route) {
      return null;
    }

    return routes.find((r) => (route.id != null && r.id === route.id) || (!!route.name && r.name === route.name)) || null;
  }

  private persistSelectedRoute(route: IRoute) {
    const storeId = this.selectedStore?.id || this._storeResolver.selectedStore.value?.id;
    if (!storeId || !route) {
      return;
    }

    localStorage.setItem(
      this.getSelectedRouteStorageKey(storeId),
      JSON.stringify({ id: route.id, name: route.name })
    );
  }

  private getStoredSelectedRoute(storeId: number): Partial<IRoute> | null {
    const storedRoute = localStorage.getItem(this.getSelectedRouteStorageKey(storeId));
    if (!storedRoute) {
      return null;
    }

    try {
      const parsedRoute = JSON.parse(storedRoute) as Partial<IRoute>;
      return parsedRoute?.id != null || parsedRoute?.name ? parsedRoute : null;
    } catch {
      localStorage.removeItem(this.getSelectedRouteStorageKey(storeId));
      return null;
    }
  }

  private getSelectedRouteStorageKey(storeId: number): string {
    return `${this.selectedRouteStoragePrefix}:${storeId}`;
  }

  private normalizeRoutesForStore(routes: any[], statuses: IOrderStatusModel[], orderTypes: IOrderType[]): IRoute[] {
    if (!routes?.length) {
      return [];
    }

    const statusById = this.buildStatusById(statuses);
    const orderTypeById = this.buildOrderTypeById(orderTypes);
    const orderTypeSortIndex = this.buildOrderTypeSortIndex(orderTypes);
    const storeOrderTypeIds = new Set(Array.from(orderTypeById.keys()));
    const shouldFilterByStoreOrderTypes = storeOrderTypeIds.size > 0;

    return routes
      .map((route) => {
        const orderTypeList = this.normalizeRouteOrderTypes(route, orderTypeById);
        return {
          id: Number(route?.id || 0),
          name: String(route?.name || 'Route'),
          orderStatusList: this.normalizeRouteStatuses(route, statusById),
          orderTypeList: shouldFilterByStoreOrderTypes
            ? orderTypeList.filter((orderType) => storeOrderTypeIds.has(Number(orderType.id)))
            : orderTypeList,
        } as IRoute;
      })
      .filter((route) => route.name && (!shouldFilterByStoreOrderTypes || route.orderTypeList.length > 0))
      .sort((a, b) => this.getRouteOrderTypeSortIndex(a, orderTypeSortIndex) - this.getRouteOrderTypeSortIndex(b, orderTypeSortIndex));
  }

  private normalizeRouteStatuses(route: any, statusById: Map<number, string>): IOrderStatusModel[] {
    if (Array.isArray(route?.orderStatusList) && route.orderStatusList.length) {
      return route.orderStatusList
        .map((status: any) => {
          const id = status?.id ?? status?.orderStatusId;
          return {
            id,
            status: status?.status || status?.orderStatusName || statusById.get(Number(id)) || '',
          };
        })
        .filter((status: IOrderStatusModel) => !!status.status);
    }

    return (route?.orderStatusIds || [])
      .map((statusId: number) => ({
        id: statusId,
        status: statusById.get(Number(statusId)) || '',
      }))
      .filter((status: IOrderStatusModel) => !!status.status);
  }

  private normalizeRouteOrderTypes(route: any, orderTypeById: Map<number, string>): IOrderType[] {
    if (Array.isArray(route?.orderTypeList) && route.orderTypeList.length) {
      return route.orderTypeList
        .map((orderType: any) => {
          const id = this.getOrderTypeIdFromModel(orderType);
          return {
            id,
            type: this.getOrderTypeNameFromModel(orderType) || orderTypeById.get(id) || String(id),
          };
        })
        .filter((orderType: IOrderType) => orderType.id != null && !Number.isNaN(Number(orderType.id)));
    }

    return (route?.orderTypeIds || [])
      .map((orderTypeId: number) => ({
        id: Number(orderTypeId),
        type: orderTypeById.get(Number(orderTypeId)) || String(orderTypeId),
      }))
      .filter((orderType: IOrderType) => orderType.id != null && !Number.isNaN(Number(orderType.id)));
  }

  private buildStatusById(statuses: IOrderStatusModel[]): Map<number, string> {
    const statusById = new Map<number, string>();
    (statuses || []).forEach((status: any) => {
      const id = Number(status?.id);
      if (!Number.isNaN(id) && status?.status) {
        statusById.set(id, status.status);
      }
    });
    return statusById;
  }

  private buildOrderTypeById(orderTypes: IOrderType[]): Map<number, string> {
    const orderTypeById = new Map<number, string>();
    (orderTypes || []).forEach((orderType: any) => {
      const id = this.getOrderTypeIdFromModel(orderType);
      const type = this.getOrderTypeNameFromModel(orderType);
      if (!Number.isNaN(id) && type) {
        orderTypeById.set(id, type);
      }
    });
    return orderTypeById;
  }

  private buildOrderTypeSortIndex(orderTypes: IOrderType[]): Map<number, number> {
    const orderTypeSortIndex = new Map<number, number>();
    (orderTypes || []).forEach((orderType: any, index) => {
      const id = this.getOrderTypeIdFromModel(orderType);
      if (!Number.isNaN(id) && !orderTypeSortIndex.has(id)) {
        orderTypeSortIndex.set(id, index);
      }
    });
    return orderTypeSortIndex;
  }

  private getRouteOrderTypeSortIndex(route: IRoute, orderTypeSortIndex: Map<number, number>): number {
    const indexes = (route?.orderTypeList || [])
      .map((orderType) => orderTypeSortIndex.get(Number(orderType.id)))
      .filter((index) => index != null);
    return indexes.length ? Math.min(...indexes) : Number.MAX_SAFE_INTEGER;
  }

  /**
   * set orders
   */
  setOrders(orders) {
    const normalizedOrders = this.normalizeOrders(orders);
    const withLineDone = normalizedOrders ? this._kdsLineDoneState.applyDoneFlagsToOrders(normalizedOrders) : normalizedOrders;
    const visibleOrders = this.sortOrdersByPreference(this.applyPendingOrderActions(withLineDone));
    if (!this.orders.value) {
      this.orders.next(visibleOrders);
      this.setOrdersTimer(this.orders.value);
    } else {
      if (!visibleOrders) {
        this.orders.next(null);
        return;
      }
      visibleOrders.forEach(order => {
        const tempOrder = this.orders.value.find(e => e.id == order.id);
        if (!tempOrder) {
          if (this.enableOrderSound.value) {
            this._audioService.playAudio('notifyBeep');
          }
        }
      });
      this.orders.next(visibleOrders);
    }
  }

  private applyPendingOrderActions(orders: IOrderModel[] | null): IOrderModel[] | null {
    this.pruneExpiredPendingOrderActions();
    if (!orders?.length || (!this.pendingStatusTransitions.size && !this.pendingRemovedOrders.size)) {
      return orders;
    }

    return orders.reduce((visibleOrders: IOrderModel[], order) => {
      if (order?.id == null) {
        visibleOrders.push(order);
        return visibleOrders;
      }

      const orderId = this.orderIdKey(order.id);
      if (this.pendingRemovedOrders.has(orderId)) {
        return visibleOrders;
      }

      const transition = this.pendingStatusTransitions.get(orderId);
      if (!transition) {
        visibleOrders.push(order);
        return visibleOrders;
      }

      if (this.isStatusName(this.getOrderStatusName(order), transition.targetStatus)) {
        this.pendingStatusTransitions.delete(orderId);
        visibleOrders.push(order);
        return visibleOrders;
      }

      if (transition.visibleStatusModel) {
        visibleOrders.push({
          ...order,
          orderStatusId: transition.visibleStatusModel.id ?? order.orderStatusId,
          orderStatusModel: transition.visibleStatusModel,
        });
      }

      return visibleOrders;
    }, []);
  }

  private subscribeOrderSortDirection() {
    this._settingsResolver.kdsOrderSortDirection.pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged(),
    ).subscribe(() => {
      const currentOrders = this.orders.value;
      if (currentOrders) {
        this.orders.next(this.sortOrdersByPreference(currentOrders));
      }
    });
  }

  private sortOrdersByPreference<T extends IOrderModel>(orders: T[] | null): T[] | null {
    if (!orders?.length) {
      return orders;
    }

    const direction = this._settingsResolver.kdsOrderSortDirection.value;
    const directionMultiplier = direction === KdsOrderSortDirection.OldestFirst ? 1 : -1;
    return [...orders].sort((a, b) => {
      const aTime = this.getOrderSortTime(a);
      const bTime = this.getOrderSortTime(b);
      if (aTime == null && bTime == null) {
        return 0;
      }
      if (aTime == null) {
        return 1;
      }
      if (bTime == null) {
        return -1;
      }
      if (aTime !== bTime) {
        return (aTime - bTime) * directionMultiplier;
      }
      return (this.getOrderSortId(a) - this.getOrderSortId(b)) * directionMultiplier;
    });
  }

  private getOrderSortTime(order: IOrderModel): number | null {
    const timestamp = order?.orderDate
      ?? (order as any)?.createdDate
      ?? order?.kdsCommittedAt
      ?? order?.kdsReleaseAt
      ?? order?.scheduledPickupAt
      ?? (order as any)?.modifiedDate;
    const orderTime = Number(timestamp);
    return Number.isFinite(orderTime) ? orderTime : null;
  }

  private getOrderSortId(order: IOrderModel): number {
    const orderId = Number(order?.id);
    return Number.isFinite(orderId) ? orderId : 0;
  }

  private pruneExpiredPendingOrderActions() {
    const now = Date.now();
    this.pendingStatusTransitions.forEach((transition, orderId) => {
      if (transition.expiresAt <= now) {
        this.pendingStatusTransitions.delete(orderId);
      }
    });
    this.pendingRemovedOrders.forEach((expiresAt, orderId) => {
      if (expiresAt <= now) {
        this.pendingRemovedOrders.delete(orderId);
      }
    });
  }

  beginOrderStatusTransition(orderId: number | string, targetStatus: string, visibleStatusModel?: IOrderStatusModel) {
    if (orderId == null || !targetStatus) {
      return;
    }

    this.pendingStatusTransitions.set(this.orderIdKey(orderId), {
      targetStatus,
      expiresAt: Date.now() + this.statusTransitionHoldMs,
      visibleStatusModel: visibleStatusModel ? { ...visibleStatusModel } : undefined,
    });
    if (visibleStatusModel) {
      this.updateOrderStatusInLocalState(orderId, visibleStatusModel);
    } else {
      this.removeOrderFromLocalState(orderId);
    }
  }

  clearOrderStatusTransition(orderId: number | string) {
    if (orderId == null) {
      return;
    }

    this.pendingStatusTransitions.delete(this.orderIdKey(orderId));
  }

  beginOrderRemoval(orderId: number | string) {
    if (orderId == null) {
      return;
    }

    const orderIdKey = this.orderIdKey(orderId);
    this.pendingStatusTransitions.delete(orderIdKey);
    this.pendingRemovedOrders.set(orderIdKey, Date.now() + this.statusTransitionHoldMs);
    this.removeOrderFromLocalState(orderId);
  }

  private orderIdKey(orderId: number | string): string {
    return String(orderId);
  }




  /**
   * Refresh Orders
   */
  refreshOrders() {
    if (!this.canLoadOrders()) {
      this.orders.next(null);
      this.loading.next(false);
      return;
    }

    if (this.orderRefreshInFlight) {
      this.queuedOrderRefresh = true;
      return;
    }

    const requestKey = this.currentOrderRequestKey();
    this.orderRefreshInFlight = true;
    this.loading.next(true);
    this.getOrders().pipe(
      takeUntil(this.destroy$),
      takeUntil(this.cancelActiveOrderLoad$),
      catchError((error) => {
        console.error('KDS order refresh failed', error);
        this.updateConnectionState(false);
        return of(this.orders.value || []);
      }),
      finalize(() => {
        this.orderRefreshInFlight = false;
        this.loading.next(false);
        if (this.queuedOrderRefresh) {
          this.queuedOrderRefresh = false;
          this.refreshOrders();
        }
      })
    ).subscribe((res) => {
      if (requestKey !== this.currentOrderRequestKey()) {
        return;
      }
      this.updateConnectionState(true);
      this.setOrders(res);
    });
  }

  private updateConnectionState(success: boolean) {
    if (success) {
      this.lastSyncAt.next(Date.now());
      this.connectionState.next('online');
      return;
    }
    const lastSync = this.lastSyncAt.value;
    this.connectionState.next(lastSync && Date.now() - lastSync > this.staleSyncMs ? 'stale' : 'error');
  }

  /**
   * Optimistically remove an order from the local list
   * @param orderId - ID of the order to remove
   */
  removeOrderFromLocalState(orderId: number | string) {
    const currentOrders = this.orders.value;
    if (currentOrders) {
      const removedOrderId = this.orderIdKey(orderId);
      const filteredOrders = currentOrders.filter(order => this.orderIdKey(order.id) !== removedOrderId);
      this.orders.next(filteredOrders);
    }
  }

  private updateOrderStatusInLocalState(orderId: number | string, orderStatusModel: IOrderStatusModel) {
    const currentOrders = this.orders.value;
    if (!currentOrders) {
      return;
    }

    const updatedOrderId = this.orderIdKey(orderId);
    let didUpdate = false;
    const updatedOrders = currentOrders.map((order) => {
      if (order?.id == null || this.orderIdKey(order.id) !== updatedOrderId) {
        return order;
      }

      didUpdate = true;
      return {
        ...order,
        orderStatusId: orderStatusModel.id ?? order.orderStatusId,
        orderStatusModel: { ...orderStatusModel },
      };
    });

    if (didUpdate) {
      this.orders.next(updatedOrders);
    }
  }



  /**
   * Order Interval
   */
  orderTimerSub: any;
  startOrderTimer() {
    if (this.orderTimerSub) {
      return;
    }
    const timerObs = timer(0, this.orderPollIntervalMs);
    this.orderTimerSub = timerObs.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      this.refreshOrders();
    });
  }




  /**
   * Unsubscrbe order interval
   * @returns 
   */
  unsubscribeOrderInterval() {
    if (this.orderTimerSub) {
      this.orderTimerSub.unsubscribe();
      this.orderTimerSub = null;
    }
  }




  /**
   * get orders
   * @returns 
   */
  getOrders(): Observable<IOrderModel[] | null> {
    const store = this.selectedStore || this._storeResolver.selectedStore.value;
    const route = this.selectedRoute.value;
    const selectedStatus = this.selectedStatus.value?.status;
    if (route?.id && selectedStatus && store?.id) {
      // The KDS feed is authoritative: the backend now returns both committed/provisional tickets
      // AND route-mapped pending orders that were never explicitly sent (send_to_kds IS NULL). A
      // valid empty response means "nothing to show" — we no longer fan out to the legacy date-range
      // endpoints, which previously fired up to 4 queries every 5s poll while the kitchen was idle.
      // On request failure, keep whatever is already on screen rather than clearing it.
      return this.kdsService.getKdsFeed(route.id, store.id, this.feedStatusesForSelectedStatus(selectedStatus)).pipe(
        map((orders: IOrderModel[]) => this.filterOrdersByStoreDate(this.dedupeOrdersById(orders || []))),
        catchError(() => of(this.orders.value))
      );
    }
    return of(null);
  }

  private feedStatusesForSelectedStatus(selectedStatus: string): string[] {
    if (this.isStatusName(selectedStatus, OrderStatus.Accepted)) {
      return [OrderStatus.Pending, OrderStatus.Accepted];
    }
    return [selectedStatus];
  }

  private filterOrdersByStoreDate(orders: IOrderModel[]): IOrderModel[] {
    if (!orders?.length) {
      return [];
    }

    const window = this.dateUtility.getCurrentStoreDateWindow(
      this.getStoreTimeZone(),
      this.orderDateLeewayMs
    );

    return orders.filter((order) => {
      const orderTime = this.getOrderBusinessTime(order);
      return orderTime != null && orderTime >= window.from && orderTime < window.to;
    });
  }

  private getStoreTimeZone(): string {
    try {
      const basicInfo = JSON.parse(localStorage.getItem(this.basicInfoStorageKey) || 'null') as IBasicInfo | null;
      return basicInfo?.timezone || '';
    } catch {
      return '';
    }
  }

  private getOrderBusinessTime(order: IOrderModel): number | null {
    const timestamp = order?.kdsReleaseAt
      ?? order?.orderDate
      ?? (order as any)?.createdDate
      ?? order?.kdsCommittedAt
      ?? (order as any)?.modifiedDate
      ?? order?.scheduledPickupAt;
    const orderTime = Number(timestamp);
    return Number.isFinite(orderTime) ? orderTime : null;
  }

  shouldDisplayOrderInKds(order: any): boolean {
    const sendToKds = order?.sendToKds
      ?? order?.sendToKDS
      ?? order?.send_to_kds
      ?? order?.orderTypeModel?.sendToKds
      ?? order?.orderTypeModel?.sendToKDS
      ?? order?.orderTypeModel?.send_to_kds
      ?? order?.orderType?.sendToKds
      ?? order?.orderType?.sendToKDS
      ?? order?.orderType?.send_to_kds;

    return sendToKds !== false && sendToKds !== 'false' && sendToKds !== 0 && sendToKds !== '0';
  }

  isProvisionalOrder(order: any): boolean {
    const state = String(order?.kdsTicketState || '').toUpperCase();
    if (state === 'PROVISIONAL') {
      return true;
    }
    if (state === 'COMMITTED' || state === 'CANCELLED') {
      return false;
    }
    return this.isStatusName(this.getOrderStatusName(order), OrderStatus.Pending);
  }

  isCommittedOrder(order: any): boolean {
    const state = String(order?.kdsTicketState || '').toUpperCase();
    return state === 'COMMITTED' || (!state && !this.isStatusName(this.getOrderStatusName(order), OrderStatus.Pending));
  }

  private getOrderTypeIdFromModel(orderType: any): number {
    return Number(orderType?.id ?? orderType?.orderTypeId ?? orderType?.orderType?.id ?? orderType?.orderType?.orderTypeId);
  }

  private getOrderTypeNameFromModel(orderType: any): string {
    return orderType?.type
      || orderType?.orderTypeName
      || orderType?.name
      || orderType?.orderType?.type
      || orderType?.orderType?.orderTypeName
      || orderType?.orderType?.name
      || '';
  }

  private normalizeLookupName(value: any): string {
    return String(value || '').trim().toLowerCase();
  }

  isStatusName(status: any, expectedStatus: any): boolean {
    return this.normalizeLookupName(status) === this.normalizeLookupName(expectedStatus);
  }

  getOrderStatusName(order: any): string {
    return this.getOrderStatusModel(order)?.status || '';
  }

  private normalizeOrders(orders: IOrderModel[] | null): IOrderModel[] | null {
    if (!orders) {
      return orders;
    }

    return orders.map((order) => this.normalizeOrderStatus(order));
  }

  private normalizeOrderStatus(order: IOrderModel): IOrderModel {
    const orderStatusModel = this.getOrderStatusModel(order);
    if (!orderStatusModel) {
      return order;
    }

    return {
      ...order,
      orderStatusId: orderStatusModel.id ?? order.orderStatusId,
      orderStatusModel,
    };
  }

  private getOrderStatusModel(order: any): IOrderStatusModel | null {
    if (!order) {
      return null;
    }

    const modelByTopLevelId = this.findStatusById(order.orderStatusId);
    if (modelByTopLevelId) {
      return modelByTopLevelId;
    }

    const modelByNestedId = this.findStatusById(order.orderStatusModel?.id);
    if (modelByNestedId) {
      return modelByNestedId;
    }

    const statusName = order.orderStatusModel?.status || order.status || order.orderStatus;
    const modelByName = this.findStatusByName(statusName);
    if (modelByName) {
      return modelByName;
    }

    return statusName
      ? {
        id: order.orderStatusModel?.id ?? order.orderStatusId,
        status: String(statusName).trim(),
      }
      : null;
  }

  private findStatusById(id: any): IOrderStatusModel | null {
    const statusId = Number(id);
    if (Number.isNaN(statusId)) {
      return null;
    }

    const status = (this.availableStausList || []).find((item) => Number(item?.id) === statusId);
    return status ? { ...status } : null;
  }

  private findStatusByName(statusName: any): IOrderStatusModel | null {
    const normalizedStatusName = this.normalizeLookupName(statusName);
    if (!normalizedStatusName) {
      return null;
    }

    const status = (this.availableStausList || []).find((item) => this.isStatusName(item?.status, normalizedStatusName));
    return status ? { ...status } : null;
  }

  private dedupeOrdersById(orders: IOrderModel[]): IOrderModel[] {
    if (!orders?.length) {
      return [];
    }

    const seen = new Map<number | string, IOrderModel>();
    orders.forEach((order) => {
      if (order?.id == null || seen.has(order.id)) {
        return;
      }
      seen.set(order.id, order);
    });
    return Array.from(seen.values());
  }

  private currentOrderRequestKey(): string {
    const store = this.selectedStore || this._storeResolver.selectedStore.value;
    return [
      store?.id || '',
      this.selectedRoute.value?.name || '',
      this.selectedStatus.value?.status || ''
    ].join('|');
  }




  /**
   *  
   */
  setOrdersTimer(orders: any[]) {
    if (orders) {
      orders.forEach((x) => {
        // this.setCardColor(hoursTimer, minuteTimer, x);
      });
    }
  }













  /**
   * mark order to next state
   * @param status
   */
  getNextStatus(status: string, orderType: string): string {
    switch (status) {
      case OrderStatus.Pending:
        return OrderStatus.Accepted;

      case OrderStatus.Accepted:
        return 'Prepared';

      case OrderStatus.Prepared:
        return OrderType.Delivery === orderType ? 'On The Way' : 'Delivered';

      case OrderStatus.OnTheWay:
        return 'Delivered';

      default:
        break;
    }
  }




  /**
   *Get all available Status List from API 
   */
  loadAvailableStatusList() {
    if (this.availableStausList?.length) {
      return;
    }
    this.kdsService.getOrderStatusList().pipe(takeUntil(this.destroy$)).subscribe((res) => {
      this.availableStausList = res;
    });
  }




  /**
   * Update Order Status
   * @param order 
   * @returns 
   */
  updateOrderStatus(order: any): Observable<any> {
    const statusId = order?.orderStatusModel?.id ?? order?.orderStatusId;
    if (order?.id != null && statusId != null) {
      return this.kdsService.updateKdsStatus(order.id, statusId);
    }
    return this._orderService.updateOrderStatus(order);
  }

  commitKdsOrder(orderId: number | string): Observable<any> {
    return this.kdsService.commitKdsOrder(orderId);
  }

  updateKdsLineState(orderId: number | string, orderDetailId: number | string, done: boolean): Observable<any> {
    return this.kdsService.updateKdsLineState(orderId, orderDetailId, done);
  }

  deleteOrder(orderId: number | string): Observable<any> {
    return this.kdsService.deleteKdsOrder(orderId);
  }


  /**
   * Destroy subscriptions
   */
  destroySubscriptions() {
    this.unsubscribeOrderInterval();
    this.clearKdsState();
    this.destroy$.next();
    this.destroy$.unsubscribe();
    this.destroy$ = new Subject<void>();

    this.orderStream$.unsubscribe();
    this.orderStream$ = new Subject<void>();
    this.initialized = false;
  }

  clearKdsState() {
    this.queuedOrderRefresh = false;
    this.cancelActiveOrderLoad$.next();
    this.orders.next(null);
    this.loading.next(false);
    this.routes.next(null);
    this.selectedRoute.next(null);
    this.selectedStatus.next(null);
    this.availableStausList = [];
    this.selectedStore = null;
    this.pendingStatusTransitions.clear();
    this.pendingRemovedOrders.clear();
    this.connectionState.next('online');
    this.lastSyncAt.next(null);
  }



}
